import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  MedicalServices,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { dentistService } from '../../services/dentist.service';
import { Dentist } from '../../types';

const specialties = [
  'Clínico Geral',
  'Ortodontia',
  'Endodontia',
  'Periodontia',
  'Implantodontia',
  'Odontopediatria',
  'Cirurgia',
  'Prótese',
  'Estética',
];

export default function DentistsPage() {
  const navigate = useNavigate();
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [loading, setLoading] = useState(true);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    cro: '',
    specialty: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadDentists();
  }, []);

  const loadDentists = async () => {
    try {
      setLoading(true);
      const data = await dentistService.getAll();
      setDentists(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error loading dentists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewDentist = () => {
    setSelectedDentist(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      cro: '',
      specialty: '',
    });
    setFormDialogOpen(true);
  };

  const handleEdit = (dentist: Dentist) => {
    setSelectedDentist(dentist);
    setFormData({
      name: dentist.user?.name || '',
      email: dentist.user?.email || '',
      password: '',
      phone: dentist.user?.phone || '',
      cro: dentist.cro,
      specialty: dentist.specialty,
    });
    setFormDialogOpen(true);
  };

  const handleDelete = (dentist: Dentist) => {
    setSelectedDentist(dentist);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedDentist) {
      try {
        await dentistService.delete(selectedDentist.id);
        loadDentists();
        toast.success('Dentista removido com sucesso');
      } catch (error) {
        console.error('Error deleting dentist:', error);
        toast.error('Não foi possível excluir o dentista');
      }
    }
    setDeleteDialogOpen(false);
    setSelectedDentist(null);
  };

  const handleFormSubmit = async () => {
    try {
      setFormLoading(true);
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password || undefined,
        phone: formData.phone || undefined,
        cro: formData.cro,
        specialty: formData.specialty,
      };

      if (selectedDentist) {
        await dentistService.update(selectedDentist.id, payload);
        toast.success('Dentista atualizado com sucesso');
      } else {
        await dentistService.create(payload);
        toast.success('Dentista cadastrado com sucesso');
      }
      
      setFormDialogOpen(false);
      loadDentists();
    } catch (error) {
      console.error('Error saving dentist:', error);
      toast.error('Não foi possível salvar o dentista');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Dentistas
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleNewDentist}>
          Novo Dentista
        </Button>
      </Box>

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : dentists.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            p: 6,
            textAlign: 'center',
          }}
        >
          <MedicalServices sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhum dentista cadastrado
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleNewDentist}>
            Cadastrar primeiro dentista
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {dentists.map((dentist) => (
            <Grid item xs={12} sm={6} lg={4} key={dentist.id}>
              <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                      }}
                    >
                      {dentist.user?.name?.charAt(0).toUpperCase() || 'D'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600">
                        Dr(a). {dentist.user?.name || 'Sem nome'}
                      </Typography>
                      <Chip
                        label={dentist.specialty}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      CRO
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {dentist.cro}
                    </Typography>
                  </Box>

                  {dentist.user?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{dentist.user.phone}</Typography>
                    </Box>
                  )}
                  {dentist.user?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{dentist.user.email}</Typography>
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <IconButton size="small" onClick={() => handleEdit(dentist)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(dentist)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o dentista{' '}
            <strong>Dr(a). {selectedDentist?.user?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dentist Form Dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedDentist ? 'Editar Dentista' : 'Novo Dentista'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome completo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={selectedDentist ? 'Nova Senha (opcional)' : 'Senha'}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!selectedDentist}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CRO"
                value={formData.cro}
                onChange={(e) => setFormData({ ...formData, cro: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Especialidade</InputLabel>
                <Select
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  label="Especialidade"
                >
                  {specialties.map((specialty) => (
                    <MenuItem key={specialty} value={specialty}>
                      {specialty}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFormDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={
              !formData.name ||
              !formData.email ||
              (!selectedDentist && !formData.password) ||
              !formData.cro ||
              !formData.specialty ||
              formLoading
            }
          >
            {formLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
